import React, { Fragment } from 'react';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import {
  IssueStatus,
  getIssueStatus,
  getIssueStatusForClient,
  IssueSelectableStatus,
  IssueSelectableStatusWithWarning,
  IssueSelectableStatusForClient,
  IssueSelectableStatusForClientWithWarning,
  IssueState,
  IssueSelectableStatusForDoctor,
  getKenshinIssueStatusForClient,
  getKenshinIssueStatus,
} from 'shared/constants/issues';
import { Select, Icon } from 'components';

import { SectionTitle } from '../../Styles';
import { Status } from './Styles';
import { IssuePropTypes } from 'shared/propTypes/issueType';
import { Box } from '@mui/material';
import logger from 'utils/logger';
import { UserType, USER_DATA } from 'shared/constants/users';
import {
  isCurrentUserOrganizationManager,
  isCurrentUserTaskManager,
} from 'utils/privilege';
import {
  KenshinIssueSelectableStatus,
  KenshinIssueSelectableStatusForClient,
} from 'shared/constants/kenshin/issues';

const propTypes = {
  isClient: PropTypes.bool.isRequired,
  isDoctor: PropTypes.bool.isRequired,
  issue: PropTypes.shape(IssuePropTypes).isRequired,
  setConfirmModalInfo: PropTypes.func,
  setConfirmModalOpen: PropTypes.func,
  setConfirmModalUpdateData: PropTypes.func,
  isKenshin: PropTypes.bool,
};

const log = logger.createLogger('IssueDetailStatus');

const IssueDetailStatus = ({
  isClient,
  isDoctor,
  issue,
  setConfirmModalInfo,
  setConfirmModalOpen,
  setConfirmModalUpdateData,
  isKenshin,
}) => {
  const isOrganizationManagement =
    localStorage.getItem(USER_DATA.USER_TYPE) === UserType.MANAGER ||
    isCurrentUserOrganizationManager();
  const isStaff =
    localStorage.getItem(USER_DATA.USER_TYPE) === UserType.STAFF ||
    isCurrentUserTaskManager();

  const calculateNewState = (formerStatus, newStatus, formerState) => {
    if (formerState === IssueState.WARNING) {
      return IssueState.WARNING;
    } else if (
      formerStatus === IssueStatus.NEWTASK &&
      newStatus !== IssueStatus.CHECKREADY
    ) {
      return IssueState.WARNING;
    } else if (
      formerStatus === IssueStatus.CHECKREADY &&
      newStatus !== IssueStatus.DOCTORASSIGNED
    ) {
      return IssueState.WARNING;
    } else if (
      formerStatus === IssueStatus.DOCTORASSIGNED &&
      newStatus !== IssueStatus.REPORTREADY
    ) {
      return IssueState.WARNING;
    } else if (
      formerStatus === IssueStatus.REPORTREADY &&
      newStatus !== IssueStatus.SENDBACK
    ) {
      return IssueState.WARNING;
    } else if (
      formerStatus === IssueStatus.SENDBACK &&
      newStatus !== IssueStatus.DONE
    ) {
      return IssueState.WARNING;
    } else {
      return IssueState.NORMAL;
    }
  };

  const handleStatusChange = (status) => {
    if (isKenshin) {
      setConfirmModalUpdateData({ progress: status });
      setConfirmModalInfo({ destination: status });
    } else {
      const state = calculateNewState(issue.status, status, issue.state);
      if (status === IssueStatus.NEWTASK || status === IssueStatus.CHECKREADY) {
        setConfirmModalUpdateData({ status, state, userIds: [], users: [] });
      } else {
        setConfirmModalUpdateData({ status, state });
      }
      setConfirmModalInfo({ destination: status, issueState: state });
    }
    setConfirmModalOpen(true);
  };

  const getSelectOptions = (isClient, isDoctor, issueState, issueStatus) => {
    let statusOptions = [];
    // Kenshin
    if (isKenshin) {
      const statusOptions = isClient
        ? KenshinIssueSelectableStatusForClient[issue.progress]
        : KenshinIssueSelectableStatus[issue.progress];

      return statusOptions.map((status) => ({
        value: status,
        label: isClient
          ? getKenshinIssueStatusForClient(status)
          : getKenshinIssueStatus(status),
      }));
    }

    // TODO Check this might not be accessible for any user
    if (!isOrganizationManagement && !isStaff && isDoctor) {
      statusOptions = IssueSelectableStatusForDoctor[issueStatus];
    }
    if (isClient) {
      if (issueState === IssueState.WARNING) {
        statusOptions = IssueSelectableStatusForClientWithWarning[issueStatus];
      }
      statusOptions = IssueSelectableStatusForClient[issueStatus];
    } else {
      if (issueState === IssueState.WARNING) {
        statusOptions = IssueSelectableStatusWithWarning[issueStatus];
      }
      statusOptions = IssueSelectableStatus[issueStatus];
    }

    return statusOptions.map((status) => ({
      value: status,
      label: isClient
        ? getIssueStatusForClient(status)
        : getIssueStatus(status),
    }));
  };

  const getStatus = (status, isClient) => {
    if (isKenshin) {
      return isClient
        ? getKenshinIssueStatusForClient(status)
        : getKenshinIssueStatus(status);
    }
    return isClient ? getIssueStatusForClient(status) : getIssueStatus(status);
  };

  return (
    <Fragment>
      {!isKenshin && (
        <SectionTitle>{intl.get('issue_details_text_status')}</SectionTitle>
      )}
      <Box sx={{ height: '32px' }}>
        <Select
          variant="empty"
          dropdownWidth={343}
          withClearValue={false}
          name="status"
          value={isKenshin ? issue.progress : issue.status}
          options={getSelectOptions(
            isClient,
            isDoctor,
            issue.state,
            issue.status
          )}
          onChange={(status) => handleStatusChange(status)}
          renderValue={({ value: status }) => (
            <Status color={status}>
              <div>{getStatus(status, isClient)}</div>
              <Icon type="chevron-down" size={18} />
            </Status>
          )}
          renderOption={({ value: status }) => (
            <Status color={status}>{getStatus(status, isClient)}</Status>
          )}
        />
      </Box>
    </Fragment>
  );
};

IssueDetailStatus.propTypes = propTypes;

export default IssueDetailStatus;
